import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import {
  useFetchPublicClubPaymentSettingsQuery,
  useDeleteClubMemberMutation,
} from "@/api/clubsApi";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/components/providers/AuthProvider";
import { ClubPricingSelector } from "./ClubPricingSelector";

function LeaveClubSection({
  showConfirm,
  isLeaving,
  onShowConfirm,
  onCancel,
  onLeave,
}: {
  showConfirm: boolean;
  isLeaving: boolean;
  onShowConfirm: () => void;
  onCancel: () => void;
  onLeave: () => void;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    }
  }, [showConfirm]);

  return (
    <>
      <style>{`
        @keyframes leaveClubFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
      <div
        className="mt-4 overflow-hidden transition-[height] duration-200 ease-in-out"
        style={{ height: height !== undefined ? height : "auto" }}
      >
        <div ref={contentRef}>
          {!showConfirm ? (
            <div
              key="trigger"
              className="flex justify-center"
              style={{ animation: "leaveClubFadeIn 200ms ease-in-out" }}
            >
              <button
                onClick={onShowConfirm}
                className="text-body-3-medium text-text-tertiary text-center hover:underline"
              >
                Leave this club
              </button>
            </div>
          ) : (
            <div
              key="confirm"
              className="space-y-3"
              style={{ animation: "leaveClubFadeIn 200ms ease-in-out" }}
            >
              <p className="text-body-3-medium text-text-secondary text-center">
                Leave this club? You can rejoin later if the club is public or
                if you're invited again.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={onCancel}
                  className="text-body-3-medium text-text-tertiary border border-border-tertiary rounded-full px-4 py-1.5 hover:bg-surface-1 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={onLeave}
                  disabled={isLeaving}
                  className="text-body-3-medium text-accent-negative border border-border-tertiary rounded-full px-4 py-1.5 hover:bg-surface-1 transition-colors disabled:opacity-50"
                >
                  {isLeaving ? "Leaving..." : "Leave club"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

interface MembershipRequiredDialogProps {
  open: boolean;
  clubId: string;
}

export function MembershipRequiredDialog({
  open,
  clubId,
}: MembershipRequiredDialogProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: paymentSettings } = useFetchPublicClubPaymentSettingsQuery(clubId);
  const [deleteClubMember, { isLoading: isLeaving }] = useDeleteClubMemberMutation();
  const [selectedInterval, setSelectedInterval] = useState<"month" | "year">("year");
  const [loading, setLoading] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  const handleCheckout = useCallback(async () => {
    if (!paymentSettings || !user?.id) return;

    setLoading(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        setLoading(false);
        return;
      }

      const origin = window.location.origin;
      const successUrl = `${origin}/clubs/${clubId}?payment=success`;
      const cancelUrl = `${origin}/clubs/${clubId}?payment=cancelled`;

      const response = await supabase.functions.invoke("create-club-checkout", {
        body: {
          clubId,
          userId: user.id,
          paymentType: "membership",
          interval: selectedInterval,
          successUrl,
          cancelUrl,
          platform: "web",
        },
      });

      if (response.error) {
        throw response.error;
      }

      if (response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      }
    } catch (error) {
      console.error("Payment error:", error);
    } finally {
      setLoading(false);
    }
  }, [clubId, user?.id, paymentSettings, selectedInterval]);

  const handleLeaveClub = useCallback(async () => {
    if (!user?.id || !clubId) return;
    try {
      await deleteClubMember({ clubId, userId: user.id }).unwrap();
      navigate({ to: "/clubs" });
    } catch (error) {
      console.error("Failed to leave club:", error);
    }
  }, [clubId, user?.id, deleteClubMember, navigate]);

  if (!paymentSettings) return null;

  return (
    <AlertDialog open={open} onOpenChange={() => {}}>
      <AlertDialogContent
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <AlertDialogHeader>
          {/* Visually hidden title for accessibility */}
          <AlertDialogTitle className="sr-only">
            Membership required
          </AlertDialogTitle>
          <AlertDialogDescription className="text-body-2 text-text-secondary text-center">
            Unlock access and support this club by completing your subscription.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="mt-6">
          <ClubPricingSelector
            paymentSettings={paymentSettings}
            selectedInterval={selectedInterval}
            onSelectInterval={setSelectedInterval}
            onContinue={handleCheckout}
            loading={loading}
            trialDays={paymentSettings.membership_trial_days || 0}
            buttonLabel="Complete your subscription"
            buttonClassName="bg-text-primary text-text-reversed hover:bg-text-primary/90"
          />
        </div>

        <LeaveClubSection
          showConfirm={showLeaveConfirm}
          isLeaving={isLeaving}
          onShowConfirm={() => setShowLeaveConfirm(true)}
          onCancel={() => setShowLeaveConfirm(false)}
          onLeave={handleLeaveClub}
        />
      </AlertDialogContent>
    </AlertDialog>
  );
}
